import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { PartialSale, TradeDirection, TradeEntry, TradeState } from '../../models/trade-entry';
import { ApiService } from '../../services/api.service';

type TrackerSheet = 'opened' | 'results';

interface CalendarDay {
  date: string;
  day: number;
  inMonth: boolean;
  isToday: boolean;
}

interface OpenedDaySummary {
  date: string;
  trades: TradeEntry[];
  volume: number;
  risk: number;
}

interface ResultItem {
  trade: TradeEntry;
  label: string;
  pnl: number;
  r: number;
  size: number;
  price?: number;
}

interface ResultDaySummary {
  date: string;
  items: ResultItem[];
  pnl: number;
  r: number;
  volume: number;
}

@Component({
  selector: 'app-trades-tracker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './trades-tracker.component.html',
  styleUrls: ['./trades-tracker.component.scss']
})
export class TradesTrackerComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  trades: TradeEntry[] = [];
  activeSheet: TrackerSheet = 'opened';
  selectedDate = this.today();
  month = new Date().getMonth();
  year = new Date().getFullYear();

  readonly months = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];
  readonly weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    this.refresh();
    this.api.tradeChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refresh());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async refresh(): Promise<void> {
    this.trades = await firstValueFrom(this.api.getTrades());
  }

  setSheet(sheet: TrackerSheet): void {
    this.activeSheet = sheet;
  }

  previousMonth(): void {
    if (this.month === 0) {
      this.month = 11;
      this.year -= 1;
    } else {
      this.month -= 1;
    }
  }

  nextMonth(): void {
    if (this.month === 11) {
      this.month = 0;
      this.year += 1;
    } else {
      this.month += 1;
    }
  }

  goToday(): void {
    const today = new Date();
    this.month = today.getMonth();
    this.year = today.getFullYear();
    this.selectedDate = this.today();
  }

  calendarDays(): CalendarDay[] {
    const firstOfMonth = new Date(this.year, this.month, 1);
    const startOffset = (firstOfMonth.getDay() + 6) % 7;
    const start = new Date(this.year, this.month, 1 - startOffset);
    const days: CalendarDay[] = [];

    for (let index = 0; index < 42; index += 1) {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      const iso = this.toIsoDate(date);
      days.push({
        date: iso,
        day: date.getDate(),
        inMonth: date.getMonth() === this.month,
        isToday: iso === this.today()
      });
    }

    return days;
  }

  openedSummary(date: string): OpenedDaySummary {
    const trades = this.trades.filter((entry) => entry.entryDate === date);
    const volume = trades.reduce((sum, entry) => sum + this.positionVolume(entry), 0);
    const risk = trades.reduce((sum, entry) => sum + this.initialRiskTotal(entry), 0);
    return { date, trades, volume: +volume.toFixed(2), risk: +risk.toFixed(2) };
  }

  resultSummary(date: string): ResultDaySummary {
    const items = this.resultItemsForDate(date);
    const pnl = items.reduce((sum, item) => sum + item.pnl, 0);
    const r = items.reduce((sum, item) => sum + item.r, 0);
    const volume = items.reduce((sum, item) => sum + ((item.price ?? 0) * item.size), 0);
    return { date, items, pnl: +pnl.toFixed(2), r: +r.toFixed(2), volume: +volume.toFixed(2) };
  }

  activeSummary(date: string): OpenedDaySummary | ResultDaySummary {
    return this.activeSheet === 'opened' ? this.openedSummary(date) : this.resultSummary(date);
  }

  selectedOpened(): OpenedDaySummary {
    return this.openedSummary(this.selectedDate);
  }

  selectedResults(): ResultDaySummary {
    return this.resultSummary(this.selectedDate);
  }

  monthOpenedTotal(): OpenedDaySummary {
    const monthPrefix = this.monthPrefix();
    const trades = this.trades.filter((entry) => entry.entryDate?.startsWith(monthPrefix));
    const volume = trades.reduce((sum, entry) => sum + this.positionVolume(entry), 0);
    const risk = trades.reduce((sum, entry) => sum + this.initialRiskTotal(entry), 0);
    return { date: monthPrefix, trades, volume: +volume.toFixed(2), risk: +risk.toFixed(2) };
  }

  monthResultTotal(): ResultDaySummary {
    const monthPrefix = this.monthPrefix();
    const items = this.trades.flatMap((entry) => this.resultItems(entry))
      .filter((item) => this.resultDate(item.trade, item.label)?.startsWith(monthPrefix));
    const pnl = items.reduce((sum, item) => sum + item.pnl, 0);
    const r = items.reduce((sum, item) => sum + item.r, 0);
    const volume = items.reduce((sum, item) => sum + ((item.price ?? 0) * item.size), 0);
    return { date: monthPrefix, items, pnl: +pnl.toFixed(2), r: +r.toFixed(2), volume: +volume.toFixed(2) };
  }

  sheetTitle(): string {
    return this.activeSheet === 'opened' ? 'Eröffnete Trades' : 'Trade-Ergebnisse';
  }

  statusClass(value: number): string {
    if (value > 0) return 'is-positive';
    if (value < 0) return 'is-negative';
    return '';
  }

  shortStatus(status: TradeState): string {
    const map: Record<TradeState, string> = {
      Watch: 'W',
      Positioniert: 'P',
      Teilverkauf: 'T',
      Glatt: 'G',
      Storniert: 'S'
    };
    return map[status] ?? status;
  }

  private resultItemsForDate(date: string): ResultItem[] {
    return this.trades.flatMap((entry) => this.resultItems(entry)
      .filter((item) => this.resultDate(entry, item.label) === date));
  }

  private resultItems(entry: TradeEntry): ResultItem[] {
    if (entry.partialSales?.length) {
      return entry.partialSales.map((sale, index) => this.partialResultItem(entry, sale, index));
    }

    if (entry.status !== 'Glatt' || !entry.sellDate) return [];
    const pnl = this.realizedPnl(entry);
    return [{
      trade: entry,
      label: 'Verkauf',
      pnl,
      r: this.realizedR(entry, pnl),
      size: entry.sellSize ?? entry.size,
      price: entry.sellPrice
    }];
  }

  private resultDate(entry: TradeEntry, label: string): string | undefined {
    const saleIndex = Number(label.replace('Teilverkauf ', '')) - 1;
    if (entry.partialSales?.length && Number.isInteger(saleIndex) && saleIndex >= 0) {
      return entry.partialSales[saleIndex]?.date;
    }
    return entry.sellDate;
  }

  private partialResultItem(entry: TradeEntry, sale: PartialSale, index: number): ResultItem {
    return {
      trade: entry,
      label: `Teilverkauf ${index + 1}`,
      pnl: sale.pnl,
      r: this.partialRealizedR(entry, sale),
      size: sale.size,
      price: sale.price
    };
  }

  private positionVolume(entry: TradeEntry): number {
    return (entry.entryPrice ?? 0) * (entry.size ?? 0);
  }

  private initialRiskPerUnit(entry: TradeEntry): number {
    const initialStop = entry.initialStopPrice ?? entry.stopPricePrevious ?? entry.stopPrice;
    return Math.abs((entry.entryPrice ?? 0) - (initialStop ?? 0));
  }

  initialRiskTotal(entry: TradeEntry): number {
    const stored = Number(entry.initialRiskTotalEUR ?? 0);
    if (stored > 0) return +stored.toFixed(2);
    return +(this.initialRiskPerUnit(entry) * (entry.size ?? 0) + (entry.entryFee ?? 0)).toFixed(2);
  }

  private realizedPnl(entry: TradeEntry): number {
    if (entry.partialSales?.length) {
      return +entry.partialSales.reduce((sum, sale) => sum + sale.pnl, 0).toFixed(2);
    }

    const sellPrice = entry.sellPrice;
    const size = entry.sellSize ?? entry.size;
    if (sellPrice === undefined || size <= 0) return entry.realizedPnl ?? 0;

    const perShare = this.calculationDirection(entry) === 'Long'
      ? sellPrice - entry.entryPrice
      : entry.entryPrice - sellPrice;
    return +(perShare * size - (entry.entryFee ?? 0) - (entry.sellFee ?? 0)).toFixed(2);
  }

  private realizedR(entry: TradeEntry, pnl: number): number {
    const risk = this.initialRiskTotal(entry);
    if (risk <= 0) return 0;
    return +(pnl / risk).toFixed(2);
  }

  private partialRealizedR(entry: TradeEntry, sale: PartialSale): number {
    const riskPerUnit = this.initialRiskPerUnit(entry);
    const proportionalEntryFee = (entry.entryFee ?? 0) * (sale.size / (entry.size || sale.size || 1));
    const saleRisk = riskPerUnit * sale.size + proportionalEntryFee + (sale.fee ?? 0);
    if (saleRisk <= 0) return 0;
    return +(sale.pnl / saleRisk).toFixed(2);
  }

  private calculationDirection(entry: TradeEntry): TradeDirection {
    return entry.certificate ? 'Long' : entry.direction;
  }

  private monthPrefix(): string {
    return `${this.year}-${String(this.month + 1).padStart(2, '0')}`;
  }

  private today(): string {
    return this.toIsoDate(new Date());
  }

  private toIsoDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
